import { QueryCache, QueryClient } from "@tanstack/react-query";

// --- Global auth-aware retry -------------------------------------------------
// On a cold tab, or during a silent token renewal, a query can fire before the
// access token is in localStorage (or while it is briefly expired) and the API
// answers 401. Left alone that surfaces as a permanent blank (e.g. the
// homepage) because the screen has no error UI and the token is never
// refreshed. Here we (1) detect auth errors, (2) kick off a single de-duped
// silent token renewal, and (3) let the query retry after a short delay so the
// refreshed token is picked up. This protects EVERY React Query screen.

let tokenRenewer = null;
let renewInFlight = null;
let authFailureHandler = null;

// Registered from the app bootstrap once the OIDC UserManager exists (see
// useAuthTokenLifecycle). Kept as an injected callback so this module stays
// decoupled from the auth layer.
export function registerTokenRenewer(renewFn) {
  tokenRenewer = typeof renewFn === "function" ? renewFn : null;
}

export function registerAuthFailureHandler(handler) {
  authFailureHandler = typeof handler === "function" ? handler : null;
}

// Runs the registered renewer at most once at a time; concurrent 401s (e.g.
// homepage + nav + footer all firing on load) share the same in-flight renewal.
function ensureTokenRenewed() {
  if (!tokenRenewer) return Promise.resolve();
  if (!renewInFlight) {
    renewInFlight = Promise.resolve()
      .then(() => tokenRenewer())
      .catch(() => {}) // the renewer reports its own failures
      .finally(() => {
        renewInFlight = null;
      });
  }
  return renewInFlight;
}

// True when an error means the API rejected the session: a 401, however the
// service threw it — a raw Response (`throw response`), an Error whose message
// carries the status, or an axios-style `{ response: { status } }`.
//
// A 403 is not a session error. The API answers 401 for every missing, invalid
// or expired token; its 403s, and the WAF's, are refusals of one request. Treating
// them as expiry signed the user out, the silent sign-in brought them straight
// back to the same 403, and the app looped.
export function isAuthError(error) {
  if (!error) {
    return false;
  }
  const status = error.status ?? error.response?.status ?? error.cause?.status;
  if (typeof status === "number") {
    return status === 401;
  }
  const message = typeof error === "string" ? error : error?.message;
  if (typeof message !== "string" || /\b403\b/.test(message)) {
    return false;
  }
  return /\b401\b/.test(message) || /unauthor/i.test(message);
}

// QueryCache onError runs after retries are exhausted. At that point renewal
// has already failed to recover the request, so re-establish the session.
const queryCache = new QueryCache({
  onError: (error) => {
    if (isAuthError(error)) authFailureHandler?.();
  },
});

// Central singleton QueryClient so services (non-hook code) and React components share the same cache.
const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 min — vehicle data doesn't change mid-session
      gcTime: 30 * 60 * 1000, // 30 min cache retention
      refetchOnWindowFocus: false, // dealer tablets sit idle; don't refetch
      // Non-auth errors keep the previous behavior (1 retry). Auth errors get
      // one extra attempt so the just-renewed token can be used.
      retry: (failureCount, error) => {
        if (isAuthError(error)) {
          if (failureCount === 0) {
            ensureTokenRenewed();
          }
          return failureCount < 2;
        }
        return failureCount < 1;
      },
      // Give the silent renew time to land before retrying a 401; otherwise
      // keep React Query's default exponential backoff.
      retryDelay: (failureCount, error) =>
        isAuthError(error)
          ? 1200 + failureCount * 800
          : Math.min(1000 * 2 ** failureCount, 30000),
    },
  },
});

export default queryClient;
